<template>
  <div class="home">
    <h2>App versions on test</h2>

    <b-button @click="raisePopup({ target: $event.target })" variant="primary">
      Add new
    </b-button>

    <!-- Info modal -->
    <b-modal
      id="appVersionsOnTestModal"
      v-bind:title="
        this.isEdit ? 'Edit version on test' : 'New version on test'
      "
      @ok="appVersionOnTestFormSubmit"
    >
      <b-form>
        <input
          id="newAppVersionsOnTestInput0"
          type="hidden"
          v-model="form.id"
        />
        <b-form-group
          id="newAppVersionsOnTestInputGroup1"
          label="Platform ID:"
          label-for="newAppVersionsOnTestInput1"
        >
          <b-form-input
            id="newAppVersionsOnTestInput1"
            type="number"
            v-model="form.platformId"
            placeholder="Enter Platform ID"
          />
        </b-form-group>

        <b-form-group
          id="newAppVersionsOnTestInputGroup2"
          label="Whitelabel ID:"
          label-for="newAppVersionsOnTestInput2"
        >
          <b-form-input
            id="newAppVersionsOnTestInput2"
            type="number"
            v-model="form.whitelabelId"
            placeholder="Enter Whitelabel ID"
          />
        </b-form-group>

        <b-form-group
          id="newAppVersionsOnTestInputGroup3"
          label="App version:"
          label-for="newAppVersionsOnTestInput3"
        >
          <b-form-input
            id="newAppVersionsOnTestInput3"
            type="text"
            v-model="form.appVersion"
            placeholder="Enter App version"
          />
        </b-form-group>

        <b-form-group id="newAppVersionsOnTestGroup4">
          <b-form-checkbox v-model="formIsPublished"
            >Is settings published?</b-form-checkbox
          >
        </b-form-group>
      </b-form>
    </b-modal>

    <b-container fluid>
      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @filtered="onFiltered"
        ref="appVersionsOnTest"
        class="appVersionsOnTest"
      >
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            class="m-1"
            @click="row.toggleDetails"
            variant="info"
          >
            …
          </b-button>
          <b-button
            size="sm"
            class="m-1"
            variant="warning"
            @click="
              raisePopup({
                target: $event.target,
                isEdit: true,
                appVersionsOnTest: row.item
              })
            "
          >
            ✎
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            @click="
              removeAppVersionOnTest({
                target: $event.target,
                appVersionOnTest: row.item
              })
            "
          >
            X
          </b-button>
        </template>
        <template v-slot:cell(isPublished)="row">
          {{ row.value ? 'Yes' : 'No' }}
        </template>

        <template v-slot:row-details="row">
          <b-card>
            <ul>
              <li v-for="(value, key) in row.item" :key="key">
                {{ key }}: {{ value }}
              </li>
            </ul>
          </b-card>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */

const originalForm = {
  id: null,
  platformId: null,
  whitelabelId: null,
  appVersion: null,
  isPublished: null
};

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: null,
      form: {
        ...originalForm
      },
      isEdit: false
    };
  },
  computed: {
    formIsPublished: {
      get: function() {
        return !!this.form.isPublished;
      },
      set: function(newValue) {
        this.form.isPublished = newValue;
      }
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'platformId',
          label: 'Platform Id',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'whitelabelId',
          label: 'Whitelabel Id',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'appVersion',
          label: 'App version',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'isPublished',
          label: 'isPublished',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'updatedAt',
          label: 'Updated',
          sortable: true,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.appVersionsOnTest.count;
    }
  },
  methods: {
    raisePopup({ target, isEdit = false, appVersionsOnTest = null }) {
      this.isEdit = isEdit;
      if (appVersionsOnTest && isEdit) {
        this.form = Object.keys(originalForm).reduce(
          (acc, current) =>
            appVersionsOnTest[current]
              ? {
                  ...acc,
                  [current]: String(appVersionsOnTest[current])
                }
              : acc,
          {}
        );
      } else {
        this.appVersionOnTestFormReset();
      }
      this.$root.$emit('bv::show::modal', 'appVersionsOnTestModal', target);
    },
    removeAppVersionOnTest({ appVersionOnTest = null }) {
      if (!appVersionOnTest) {
        return;
      }
      const promise = this.$store.dispatch(
        'removeAppVersionOnTest',
        appVersionOnTest.id
      );

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        this.$refs.appVersionsOnTest.refresh();
      });
    },
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
    },
    items(ctx) {
      const promise = this.$store.dispatch('getAppVersionsOnTest', ctx);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        const items = this.$store.state.appVersionsOnTest.rows.map(ver => {
          if (ver.isPublished) {
            ver._rowVariant = 'success';
          }
          return ver;
        });
        // Must return an array of items or an empty array if an error occurred
        return items || [];
      });
    },
    appVersionOnTestFormReset() {
      this.form = { ...originalForm };
    },
    async appVersionOnTestFormSubmit(event) {
      if (!this.isEdit) {
        await this.$store.dispatch('addAppVersionOnTest', {
          appVersionOnTest: this.form,
          event
        });
      } else {
        await this.$store.dispatch('editAppVersionOnTest', {
          appVersionOnTest: this.form,
          event
        });
      }

      this.appVersionOnTestFormReset();
      this.$refs.appVersionsOnTest.refresh();
    }
  }
};
</script>
<style scoped type="text/scss">
.block {
  border-bottom: #dadada 1px solid;
  padding-bottom: 1em;
  margin-bottom: 3em;
}
.container {
  max-width: none;
}
.appVersionsOnTest {
  font-size: 0.8rem;
}
.btn {
  font-size: 0.8rem;
}

td[data-label='Actions'] {
  white-space: nowrap;
}
</style>
